import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Calendar, Clock, Check, XCircle, Mail, Phone } from 'lucide-react';
import {
  getAppointments,
  disableTimeSlot,
  enableTimeSlot,
  getDisabledTimeSlots,
  updateAppointmentStatus,
  isPastAppointment,
  APPOINTMENT_STATUS_LABELS,
  AppointmentStatus,
} from '../services/appointmentService';

const Container = styled.div`
  padding: 20px;
  background: #f8f9fa;
  min-height: 100vh;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  background: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h2`
  color: #333;
  font-size: 1.8rem;
  font-weight: 300;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Button = styled.button<{ $variant?: 'primary' | 'danger' }>`
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;

  ${props => {
    switch (props.$variant) {
      case 'danger':
        return `
          background: #dc3545;
          color: white;
          &:hover { background: #c82333; }
        `;
      default:
        return `
          background: #007bff;
          color: white;
          &:hover { background: #0056b3; }
        `;
    }
  }}
`;

const AppointmentsList = styled.div`
  background: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
`;

const AppointmentCard = styled.div<{ $status: string }>`
  padding: 15px;
  border: 2px solid ${props => props.$status === 'pending' ? '#ffc107' : props.$status === 'confirmed' ? '#28a745' : '#e0e0e0'};
  border-radius: 10px;
  margin-bottom: 15px;
  background: ${props => props.$status === 'pending' ? '#fff3cd' : props.$status === 'confirmed' ? '#d4edda' : '#f8f9fa'};
`;

const AppointmentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
`;

const AppointmentInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
  font-size: 0.9rem;
`;

const StatusBadge = styled.span<{ $status: string }>`
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  background: ${props => 
    props.$status === 'pending' ? '#ffc107' : 
    props.$status === 'confirmed' ? '#28a745' : 
    '#6c757d'};
  color: ${props => props.$status === 'confirmed' ? 'white' : '#333'};
`;

const TimeSlotManagement = styled.div`
  background: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const FormGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 15px;

  input, select {
    padding: 10px;
    border: 2px solid #e1e5e9;
    border-radius: 8px;
    font-size: 14px;
    flex: 1;
  }
`;

const TimeSlotList = styled.div`
  margin-top: 20px;
`;

const SectionTitle = styled.h3`
  margin-bottom: 20px;
  color: #333;
  font-size: 1.2rem;
`;

const SelectInput = styled.select`
  padding: 10px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 14px;
  flex: 1;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const TimeSlotItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 10px;
`;

const TimeSlotInfo = styled.div`
  flex: 1;
`;

const TimeSlotReason = styled.div`
  font-size: 0.9rem;
  color: #666;
  margin-top: 5px;
`;

const EmptyMessage = styled.p`
  color: #666;
`;

const SubSectionTitle = styled.h4`
  margin-bottom: 15px;
  color: #333;
`;

const Tabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  border-bottom: 1px solid #e5e5e5;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 10px 18px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: ${props => (props.$active ? 600 : 500)};
  color: ${props => (props.$active ? '#1a1a1a' : '#888')};
  border-bottom: 2px solid ${props => (props.$active ? '#1a1a1a' : 'transparent')};
  margin-bottom: -1px;
  transition: all 0.2s ease;

  &:hover {
    color: #1a1a1a;
  }
`;

const StatusSelect = styled.select`
  padding: 7px 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.85rem;
  font-family: inherit;
  cursor: pointer;
  background: white;

  &:focus {
    outline: none;
    border-color: #1a1a1a;
  }
`;

const CardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
  flex-wrap: wrap;
`;

const AppointmentManagement: React.FC = () => {
  const [appointments, setAppointments] = useState<any[]>([]);
  const [disabledSlots, setDisabledSlots] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [reason, setReason] = useState('');
  const [activeTab, setActiveTab] = useState<'active' | 'past'>('active');
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const timeSlots = [
    '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'
  ];

  useEffect(() => {
    loadAppointments();
    loadDisabledSlots();
  }, []);

  const loadAppointments = async () => {
    try {
      setLoading(true);
      const data = await getAppointments();
      console.log('Appointments loaded:', data);
      setAppointments(data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading appointments:', error);
      setLoading(false);
    }
  };

  const loadDisabledSlots = async () => {
    const data = await getDisabledTimeSlots();
    setDisabledSlots(data);
  };

  const handleStatusChange = async (appointment: any, newStatus: AppointmentStatus) => {
    if (newStatus === appointment.status) return;

    if (newStatus === 'cancelled') {
      const ok = window.confirm(
        'Randevu iptal edilecek ve bu saat yeniden randevuya açılacak. Onaylıyor musunuz?'
      );
      if (!ok) return;
    }

    setUpdatingId(appointment.id);
    const success = await updateAppointmentStatus(appointment.id, newStatus, {
      appointmentDate: appointment.appointmentDate,
      appointmentTime: appointment.appointmentTime,
    });
    setUpdatingId(null);

    if (success) {
      setAppointments(prev =>
        prev.map(a => (a.id === appointment.id ? { ...a, status: newStatus } : a))
      );
    } else {
      alert('Randevu durumu güncellenemedi.');
    }
  };

  const handleDisableTimeSlot = async () => {
    if (!selectedDate || !selectedTime) {
      alert('Lütfen tarih ve saat seçin.');
      return;
    }

    const result = await disableTimeSlot(selectedDate, selectedTime, reason);
    if (result) {
      alert('Saat kapatıldı.');
      loadDisabledSlots();
      setSelectedDate('');
      setSelectedTime('');
      setReason('');
    }
  };

  const handleEnableTimeSlot = async (date: string, time: string) => {
    const result = await enableTimeSlot(date, time);
    if (result) {
      alert('Saat açıldı.');
      loadDisabledSlots();
    }
  };

  if (loading) {
    return <Container>Yükleniyor...</Container>;
  }

  // Randevular iki gruba ayrilir:
  //  - Aktif : bugun veya ilerideki, henuz sonuclanmamis randevular
  //  - Gecmis: musteri geldi/gelmedi, iptal edildi ya da tarihi gecmis olanlar
  const activeAppointments = appointments.filter(a => !isPastAppointment(a));
  const pastAppointments = appointments.filter(a => isPastAppointment(a));
  const visibleAppointments = activeTab === 'active' ? activeAppointments : pastAppointments;

  return (
    <Container>
      <Header>
        <Title>
          <Calendar size={30} />
          Randevu Yönetimi
        </Title>
      </Header>

      <AppointmentsList>
        <Tabs>
          <Tab $active={activeTab === 'active'} onClick={() => setActiveTab('active')}>
            Aktif Randevular ({activeAppointments.length})
          </Tab>
          <Tab $active={activeTab === 'past'} onClick={() => setActiveTab('past')}>
            Geçmiş Randevular ({pastAppointments.length})
          </Tab>
        </Tabs>

        {visibleAppointments.length === 0 ? (
          <EmptyMessage>
            {activeTab === 'active'
              ? 'Bekleyen randevu bulunmuyor.'
              : 'Geçmiş randevu bulunmuyor.'}
          </EmptyMessage>
        ) : (
          visibleAppointments.map(appointment => (
            <AppointmentCard key={appointment.id} $status={appointment.status}>
              <AppointmentHeader>
                <div>
                  <h4>{appointment.firstName} {appointment.lastName}</h4>
                  <StatusBadge $status={appointment.status}>
                    {APPOINTMENT_STATUS_LABELS[appointment.status as AppointmentStatus] || appointment.status}
                  </StatusBadge>
                </div>
              </AppointmentHeader>

              <AppointmentInfo>
                <InfoItem>
                  <Mail size={16} />
                  {appointment.email}
                </InfoItem>
                <InfoItem>
                  <Phone size={16} />
                  {appointment.phone}
                </InfoItem>
                <InfoItem>
                  <Calendar size={16} />
                  {appointment.appointmentDate}
                </InfoItem>
                <InfoItem>
                  <Clock size={16} />
                  {appointment.appointmentTime}
                </InfoItem>
                <InfoItem>
                  <Clock size={16} />
                  {appointment.serviceType}
                </InfoItem>
              </AppointmentInfo>

              {appointment.message && (
                <TimeSlotReason>Not: {appointment.message}</TimeSlotReason>
              )}

              <CardActions>
                <span style={{ fontSize: '0.85rem', color: '#666' }}>Durum:</span>
                <StatusSelect
                  value={appointment.status || 'pending'}
                  disabled={updatingId === appointment.id}
                  onChange={(e) =>
                    handleStatusChange(appointment, e.target.value as AppointmentStatus)
                  }
                >
                  {(Object.keys(APPOINTMENT_STATUS_LABELS) as AppointmentStatus[]).map(key => (
                    <option key={key} value={key}>
                      {APPOINTMENT_STATUS_LABELS[key]}
                    </option>
                  ))}
                </StatusSelect>
                {updatingId === appointment.id && (
                  <span style={{ fontSize: '0.8rem', color: '#888' }}>Güncelleniyor...</span>
                )}
              </CardActions>
            </AppointmentCard>
          ))
        )}
      </AppointmentsList>

      <TimeSlotManagement>
        <SectionTitle>Saatleri Kapatma</SectionTitle>
        
        <FormGroup>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            placeholder="Tarih seçin"
            title="Tarih seçin"
          />
          <SelectInput
            value={selectedTime}
            onChange={(e) => setSelectedTime(e.target.value)}
            title="Saat seçin"
            aria-label="Saat seçin"
          >
            <option value="">Saat seçin</option>
            {timeSlots.map(time => (
              <option key={time} value={time}>{time}</option>
            ))}
          </SelectInput>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Neden (opsiyonel)"
          />
          <Button onClick={handleDisableTimeSlot}>
            <XCircle size={16} />
            Saat Kapat
          </Button>
        </FormGroup>

        <TimeSlotList>
          <SubSectionTitle>Kapalı Saatler ({disabledSlots.length})</SubSectionTitle>
          
          {disabledSlots.length === 0 ? (
            <EmptyMessage>Kapalı saat yok.</EmptyMessage>
          ) : (
            disabledSlots.map(slot => (
              <TimeSlotItem key={slot.id}>
                <TimeSlotInfo>
                  <div>
                    <strong>{slot.date}</strong> - <strong>{slot.time}</strong>
                  </div>
                  {slot.reason && (
                    <TimeSlotReason>Neden: {slot.reason}</TimeSlotReason>
                  )}
                </TimeSlotInfo>
                <Button $variant="danger" onClick={() => handleEnableTimeSlot(slot.date, slot.time)}>
                  <Check size={16} />
                  Aç
                </Button>
              </TimeSlotItem>
            ))
          )}
        </TimeSlotList>
      </TimeSlotManagement>
    </Container>
  );
};

export default AppointmentManagement;

